/**
 * customersService — CRUD completo do módulo de clientes (Plano C, Task 4).
 *
 * Expande o esqueleto mínimo da Task 6 (só `getDropdown`) com listagem,
 * detalhe, create/update/delete e associate/disassociate de contato — rotas
 * já existentes no backend saneado pela Task 2 (`@/modules/customers/router.ts`).
 */

import type { AxiosRequestConfig } from 'axios';
import type {
  CreateCustomerRequest,
  CustomerDropdownItem,
  CustomerResponse,
  CustomerServiceListResponse,
  CustomerServiceQuery,
  UpdateCustomerRequest,
} from '@troia-v3/types';

import { api } from '@/lib/api';

/**
 * Suprime o `toast.error` genérico do interceptor global (`src/lib/api.ts`)
 * em create/update — `CustomerFormPage` trata o 409 de documento duplicado
 * como erro de campo (`document`) e outros erros com toast próprio, então
 * não queremos toast duplicado. Mesmo padrão de `ticketsService.transfer`.
 */
const HANDLE_ERROR_LOCALLY: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
  _handleErrorLocally: true,
};

export const customersService = {
  /**
   * Lista leve para selectors/dropdowns cross-module —
   * `GET /api/customers/dropdown` (sem `permissionMiddleware`).
   */
  async getDropdown(search?: string): Promise<CustomerDropdownItem[]> {
    const response = await api.get('customers/dropdown', {
      params: search ? { search } : undefined,
    });
    return response.data.data;
  },

  /**
   * Listagem paginada com busca (`search`, por nome) e filtros
   * (`filters.{status,type,segment,...}`) — `GET /api/customers`.
   */
  async getCustomers(query?: CustomerServiceQuery): Promise<CustomerServiceListResponse> {
    const response = await api.get('customers', { params: query });
    return response.data.data;
  },

  /** `GET /api/customers/:id`. */
  async getCustomerById(id: string): Promise<CustomerResponse> {
    const response = await api.get(`customers/${id}`);
    return response.data.data;
  },

  /** `POST /api/customers` — 409 quando `document` já existe no tenant. */
  async createCustomer(data: CreateCustomerRequest): Promise<CustomerResponse> {
    const response = await api.post('customers', data, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /** `PUT /api/customers/:id` — mesmo 409 de `document` duplicado do create. */
  async updateCustomer(id: string, data: UpdateCustomerRequest): Promise<CustomerResponse> {
    const response = await api.put(`customers/${id}`, data, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /** `DELETE /api/customers/:id` (soft delete via `GenericRepository`). */
  async deleteCustomer(id: string): Promise<void> {
    await api.delete(`customers/${id}`);
  },

  /** `POST /api/customers/:id/contacts` — vincula um contato ao cliente. */
  async associateContact(customerId: string, contactId: string): Promise<void> {
    await api.post(`customers/${customerId}/contacts`, { contactId }, HANDLE_ERROR_LOCALLY);
  },

  /** `DELETE /api/customers/:id/contacts/:contactId` — desvincula o contato. */
  async disassociateContact(customerId: string, contactId: string): Promise<void> {
    await api.delete(`customers/${customerId}/contacts/${contactId}`);
  },
};
